import React from "react";
import { apiUrl } from "../../config/apiBase";

const prazdnyFormular = {
  bankaKod: "",
  typFixacie: "rocna",
  fixaciaRoky: "",
  fixaciaMesiace: "",
  priradenieKfixaciaRoky: "",
  urok: ""
};

function formatPercento(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return `${n.toFixed(2).replace(".", ",")} %`;
}

function cisloNaInput(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return String(n).replace(".", ",");
}

function normalizujInputCislo(v) {
  return String(v || "")
    .replace(/\s/g, "")
    .replace(",", ".");
}

function formularZFixacie(fixacia, bankaKod = "") {
  return {
    ...prazdnyFormular,
    bankaKod,
    typFixacie: fixacia?.typFixacie === "mesacna" ? "mesacna" : "rocna",
    fixaciaRoky: cisloNaInput(fixacia?.fixaciaRoky),
    fixaciaMesiace: cisloNaInput(fixacia?.fixaciaMesiace),
    priradenieKfixaciaRoky: cisloNaInput(fixacia?.priradenieKfixaciaRoky)
  };
}

function AdminHypotekySadzby() {
  const [meta, setMeta] = React.useState({
    banky: [],
    fixacie: []
  });
  const [sadzby, setSadzby] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [filterBankaKod, setFilterBankaKod] = React.useState("");
  const [filterFixaciaKey, setFilterFixaciaKey] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editSadzba, setEditSadzba] = React.useState(null);
  const [formular, setFormular] = React.useState(prazdnyFormular);
  const [modalError, setModalError] = React.useState("");
  const [modalSaving, setModalSaving] = React.useState(false);
  const [deleteSadzba, setDeleteSadzba] = React.useState(null);
  const [deleteError, setDeleteError] = React.useState("");
  const [deleting, setDeleting] = React.useState(false);

  const banky = React.useMemo(
    () => Array.isArray(meta?.banky) ? meta.banky : [],
    [meta?.banky]
  );

  const fixacie = React.useMemo(
    () => Array.isArray(meta?.fixacie) ? meta.fixacie : [],
    [meta?.fixacie]
  );

  const zobrazeneBanky = React.useMemo(
    () => banky.filter((b) => !filterBankaKod || b.bankaKod === filterBankaKod),
    [banky, filterBankaKod]
  );

  const zobrazeneFixacie = React.useMemo(
    () => fixacie.filter((f) => !filterFixaciaKey || f.key === filterFixaciaKey),
    [fixacie, filterFixaciaKey]
  );

  const sadzbyMapa = React.useMemo(() => {
    const mapa = new Map();
    sadzby.forEach((s) => {
      if (!s?.bankaKod || !s?.fixaciaKey) return;
      mapa.set(`${s.fixaciaKey}|${s.bankaKod}`, s);
    });
    return mapa;
  }, [sadzby]);

  const nacitajMeta = React.useCallback(async () => {
    const r = await fetch(apiUrl("/api/admin/hypoteky/meta"), {
      credentials: "include"
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.message || "Meta dáta sa nepodarilo načítať.");
    setMeta(data || {});
  }, []);

  const nacitajSadzby = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (filterBankaKod) params.set("bankaKod", filterBankaKod);
      if (filterFixaciaKey) params.set("fixaciaKey", filterFixaciaKey);

      const r = await fetch(apiUrl(`/api/admin/hypoteky/sadzby?${params.toString()}`), {
        credentials: "include"
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || "Úrokové sadzby sa nepodarilo načítať.");
      setSadzby(Array.isArray(data?.sadzby) ? data.sadzby : []);
    } catch (e) {
      setError(e.message || "Úrokové sadzby sa nepodarilo načítať.");
      setSadzby([]);
    } finally {
      setLoading(false);
    }
  }, [filterBankaKod, filterFixaciaKey]);

  React.useEffect(() => {
    nacitajMeta().catch((e) => setError(e.message || "Meta dáta sa nepodarilo načítať."));
  }, [nacitajMeta]);

  React.useEffect(() => {
    nacitajSadzby();
  }, [nacitajSadzby]);

  const otvorPridanie = () => {
    setEditSadzba(null);
    setFormular({
      ...prazdnyFormular,
      bankaKod: filterBankaKod || ""
    });
    setModalError("");
    setMessage("");
    setError("");
    setModalOpen(true);
  };

  const otvorPridaniePreBunku = (bankaKod, fixacia) => {
    setEditSadzba(null);
    setFormular(formularZFixacie(fixacia, bankaKod));
    setModalError("");
    setMessage("");
    setError("");
    setModalOpen(true);
  };

  const otvorUpravu = (s) => {
    setEditSadzba(s);
    setFormular({
      bankaKod: s?.bankaKod || "",
      typFixacie: s?.typFixacie === "mesacna" ? "mesacna" : "rocna",
      fixaciaRoky: cisloNaInput(s?.fixaciaRoky),
      fixaciaMesiace: cisloNaInput(s?.fixaciaMesiace),
      priradenieKfixaciaRoky: cisloNaInput(s?.priradenieKfixaciaRoky),
      urok: cisloNaInput(s?.urok)
    });
    setModalError("");
    setMessage("");
    setError("");
    setModalOpen(true);
  };

  const nastavPole = (nazov, hodnota) => {
    setFormular((prev) => ({
      ...prev,
      [nazov]: hodnota
    }));
    setModalError("");
  };

  const formularJeKompletny = React.useMemo(() => {
    if (!formular.bankaKod) return false;
    if (!normalizujInputCislo(formular.urok)) return false;
    if (formular.typFixacie === "mesacna") {
      return Boolean(
        normalizujInputCislo(formular.fixaciaMesiace) &&
        normalizujInputCislo(formular.priradenieKfixaciaRoky)
      );
    }
    return Boolean(normalizujInputCislo(formular.fixaciaRoky));
  }, [formular]);

  const ulozSadzbu = async () => {
    if (!formularJeKompletny || modalSaving) return;
    setModalSaving(true);
    setModalError("");

    try {
      const jeEdit = Boolean(editSadzba?.id);
      const body = {
        bankaKod: formular.bankaKod,
        typFixacie: formular.typFixacie,
        fixaciaRoky: normalizujInputCislo(formular.fixaciaRoky),
        fixaciaMesiace: normalizujInputCislo(formular.fixaciaMesiace),
        priradenieKfixaciaRoky: normalizujInputCislo(formular.priradenieKfixaciaRoky),
        urok: normalizujInputCislo(formular.urok)
      };

      const r = await fetch(
        apiUrl(jeEdit ? `/api/admin/hypoteky/sadzby/${editSadzba.id}` : "/api/admin/hypoteky/sadzby"),
        {
          method: jeEdit ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body)
        }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || "Úrokovú sadzbu sa nepodarilo uložiť.");

      setModalOpen(false);
      setMessage(data?.message || "Úroková sadzba bola uložená.");
      await Promise.all([nacitajMeta(), nacitajSadzby()]);
    } catch (e) {
      setModalError(e.message || "Úrokovú sadzbu sa nepodarilo uložiť.");
    } finally {
      setModalSaving(false);
    }
  };

  const potvrditVymazanie = async () => {
    if (!deleteSadzba?.id || deleting) return;
    setDeleting(true);
    setDeleteError("");

    try {
      const r = await fetch(apiUrl(`/api/admin/hypoteky/sadzby/${deleteSadzba.id}`), {
        method: "DELETE",
        credentials: "include"
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || "Úrokovú sadzbu sa nepodarilo vymazať.");

      setDeleteSadzba(null);
      setMessage(data?.message || "Úroková sadzba bola vymazaná.");
      await Promise.all([nacitajMeta(), nacitajSadzby()]);
    } catch (e) {
      setDeleteError(e.message || "Úrokovú sadzbu sa nepodarilo vymazať.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="card-section admin-users-card">
        <div className="admin-users-header">
          <div>
            <h2>Úrokové sadzby</h2>
            <div className="form_hint">
              Základné sadzby podľa banky a fixácie. Checkboxové zľavy sa spravujú v parametroch banky.
            </div>
          </div>

          <div className="admin-users-header-actions">
            <button type="button" className="action-btn" onClick={nacitajSadzby} disabled={loading}>
              {loading ? "Načítavam..." : "Obnoviť"}
            </button>
            <button type="button" className="action-btn" onClick={otvorPridanie}>
              + Pridať sadzbu
            </button>
          </div>
        </div>

        <div className="admin-users-toolbar admin-hypoteky-toolbar">
          <select
            className="form_select admin-hypoteky-filter"
            value={filterBankaKod}
            onChange={(e) => setFilterBankaKod(e.target.value)}
          >
            <option value="">Všetky banky</option>
            {banky.map((b) => (
              <option key={b.bankaKod} value={b.bankaKod}>
                {b.bankaKod}
              </option>
            ))}
          </select>

          <select
            className="form_select admin-hypoteky-filter"
            value={filterFixaciaKey}
            onChange={(e) => setFilterFixaciaKey(e.target.value)}
          >
            <option value="">Všetky fixácie</option>
            {fixacie.map((f) => (
              <option key={f.key} value={f.key}>
                {f.labelFixacie}
              </option>
            ))}
          </select>
        </div>

        {error && <div className="admin-users-message admin-users-message--error">{error}</div>}
        {message && <div className="admin-users-message">{message}</div>}

        <div className="admin-users-table-wrap">
          <table className="admin-hypoteky-matrix">
            <thead>
              <tr>
                <th>Fixácia</th>
                {zobrazeneBanky.map((b) => (
                  <th key={b.bankaKod}>{b.bankaKod}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && zobrazeneFixacie.map((fixacia) => (
                <tr key={fixacia.key}>
                  <th>{fixacia.labelFixacie} Fix</th>
                  {zobrazeneBanky.map((b) => {
                    const sadzba = sadzbyMapa.get(`${fixacia.key}|${b.bankaKod}`);
                    return (
                      <td key={`${fixacia.key}-${b.bankaKod}`}>
                        <button
                          type="button"
                          className={`admin-hypoteky-cell ${sadzba ? "has-value" : "is-empty"}`}
                          onClick={() => sadzba ? otvorUpravu(sadzba) : otvorPridaniePreBunku(b.bankaKod, fixacia)}
                          title={sadzba ? "Upraviť sadzbu" : "Pridať sadzbu"}
                        >
                          {sadzba ? formatPercento(sadzba.urok) : "-"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {loading && <div className="admin-users-empty">Načítavam sadzby...</div>}
          {!loading && (!zobrazeneFixacie.length || !zobrazeneBanky.length) && (
            <div className="admin-users-empty">Nenašli sa žiadne sadzby.</div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="pdf-modal-overlay" onClick={() => !modalSaving && setModalOpen(false)}>
          <div className="pdf-modal admin-hypoteky-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-modal__header">
              <h2 className="pdf-modal__title">
                {editSadzba ? "Upraviť úrokovú sadzbu" : "Pridať úrokovú sadzbu"}
              </h2>
            </div>

            <div className="pdf-modal__body">
              <div className="admin-modal-grid">
                <div className="pdf-modal__field">
                  <label className="pdf-modal__label">Banka</label>
                  <select
                    className="pdf-modal__input"
                    value={formular.bankaKod}
                    onChange={(e) => nastavPole("bankaKod", e.target.value)}
                  >
                    <option value="">Vyber banku</option>
                    {banky.map((b) => (
                      <option key={b.bankaKod} value={b.bankaKod}>
                        {b.bankaKod}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pdf-modal__field">
                  <label className="pdf-modal__label">Typ fixácie</label>
                  <select
                    className="pdf-modal__input"
                    value={formular.typFixacie}
                    onChange={(e) => nastavPole("typFixacie", e.target.value)}
                  >
                    <option value="rocna">Ročná fixácia</option>
                    <option value="mesacna">Atypická mesačná fixácia</option>
                  </select>
                </div>

                {formular.typFixacie === "rocna" ? (
                  <div className="pdf-modal__field">
                    <label className="pdf-modal__label">Fixácia (roky)</label>
                    <input
                      className="pdf-modal__input"
                      type="text"
                      inputMode="numeric"
                      value={formular.fixaciaRoky}
                      onChange={(e) => nastavPole("fixaciaRoky", e.target.value.replace(/\D/g, ""))}
                    />
                  </div>
                ) : (
                  <>
                    <div className="pdf-modal__field">
                      <label className="pdf-modal__label">Fixácia (mesiace)</label>
                      <input
                        className="pdf-modal__input"
                        type="text"
                        inputMode="numeric"
                        value={formular.fixaciaMesiace}
                        onChange={(e) => nastavPole("fixaciaMesiace", e.target.value.replace(/\D/g, ""))}
                      />
                    </div>
                    <div className="pdf-modal__field">
                      <label className="pdf-modal__label">Priradenie k fixácii (roky)</label>
                      <input
                        className="pdf-modal__input"
                        type="text"
                        inputMode="numeric"
                        value={formular.priradenieKfixaciaRoky}
                        onChange={(e) => nastavPole("priradenieKfixaciaRoky", e.target.value.replace(/\D/g, ""))}
                      />
                    </div>
                  </>
                )}

                <div className="pdf-modal__field">
                  <label className="pdf-modal__label">Úroková sadzba</label>
                  <input
                    className="pdf-modal__input"
                    type="text"
                    inputMode="decimal"
                    value={formular.urok}
                    onChange={(e) => nastavPole("urok", e.target.value.replace(/[^0-9,.]/g, ""))}
                  />
                </div>
              </div>

              <div className="form_hint admin-hypoteky-help">
                Zadávaj iba číslo. Percento sa doplní automaticky. Checkboxové zľavy sa do sadzby premietajú cez parametre banky.
              </div>

              {modalError && <div className="admin-modal-error">{modalError}</div>}
            </div>

            <div className="pdf-modal__footer">
              {editSadzba && (
                <button
                  type="button"
                  className="action-btn admin-danger-btn"
                  disabled={modalSaving}
                  onClick={() => {
                    setDeleteError("");
                    setDeleteSadzba(editSadzba);
                    setModalOpen(false);
                  }}
                >
                  Vymazať
                </button>
              )}
              <button type="button" className="action-btn" disabled={modalSaving} onClick={() => setModalOpen(false)}>
                Zrušiť
              </button>
              <button type="button" className="action-btn" disabled={!formularJeKompletny || modalSaving} onClick={ulozSadzbu}>
                {modalSaving ? "Ukladám..." : "Uložiť"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteSadzba && (
        <div className="pdf-modal-overlay" onClick={() => !deleting && setDeleteSadzba(null)}>
          <div className="pdf-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-modal__header">
              <h2 className="pdf-modal__title">Vymazať úrokovú sadzbu</h2>
            </div>
            <div className="pdf-modal__body">
              <p>
                Naozaj chcete vymazať sadzbu <strong>{deleteSadzba.bankaKod} {deleteSadzba.labelFixacie}</strong>?
              </p>
              {deleteError && <div className="admin-modal-error">{deleteError}</div>}
            </div>
            <div className="pdf-modal__footer">
              <button type="button" className="action-btn" disabled={deleting} onClick={() => setDeleteSadzba(null)}>
                Zrušiť
              </button>
              <button type="button" className="action-btn admin-danger-btn" disabled={deleting} onClick={potvrditVymazanie}>
                {deleting ? "Mažem..." : "Vymazať"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminHypotekySadzby;
